import { type Reducer, useCallback, useReducer, useEffect } from 'react'
import { useQueryParams, BooleanParam } from 'use-query-params'
import pick from 'lodash/pick'
import omitBy from 'lodash/omitBy'
import isNil from 'lodash/isNil'
import { numberQueryParamEncoder } from 'utils/query-params/number.utils'
import { stringQueryParamEncoder } from 'utils/query-params/string.utils'
import { useSelectionManager } from 'utils/selection-manager/SelectionManager'
import { defaultItemsPerPageValues } from '@retailer-platform/shared-components'
import { makeEnumQueryParamEncoder } from 'utils/query-params/makeEnumQueryParamEncoder'
import { type ProductSearchParams } from './types'

interface UseSearchParamsOptions {
  defaultState: ProductSearchParams
}

type Actions =
  | {
      type: 'replace'
      values: ProductSearchParams
    }
  | {
      type: 'update'
      values: Partial<ProductSearchParams>
    }
  | {
      type: 'setPage'
      value: ProductSearchParams['page']
    }

const reducer: Reducer<ProductSearchParams, Actions> = (state, action) => {
  switch (action.type) {
    case 'replace':
      return { ...action.values }

    case 'update':
      // Any changes in params will trigger changing the page to 1
      return { ...state, ...action.values, page: 1 }

    case 'setPage':
      return { ...state, page: action.value }

    default:
      throw new Error(`Unrecognized action: ${JSON.stringify(action)}`)
  }
}

const queryParamsEncoders = {
  searchTerm: stringQueryParamEncoder,
  searchType: stringQueryParamEncoder,
  departmentId: stringQueryParamEncoder,
  aisleId: stringQueryParamEncoder,
  page: numberQueryParamEncoder,
  locale: stringQueryParamEncoder,
  hasPrimaryImageUrl: BooleanParam,
  isStoreBrandProduct: BooleanParam,
  limit: makeEnumQueryParamEncoder(defaultItemsPerPageValues, numberQueryParamEncoder),
  creationDateRange: stringQueryParamEncoder,
  creationDateCustomStart: stringQueryParamEncoder,
  creationDateCustomEnd: stringQueryParamEncoder,
}

const queryParamsDefinition = {
  queryParamsEncoders,
  queryParamsKeys: Object.keys(queryParamsEncoders),
}

export const useSearchParams = ({ defaultState }: UseSearchParamsOptions) => {
  const { queryParamsEncoders, queryParamsKeys } = queryParamsDefinition
  const [queryParams, setQueryParams] = useQueryParams(queryParamsEncoders)
  const { clearSelection } = useSelectionManager()

  const [state, dispatch] = useReducer(reducer, { ...defaultState, ...omitBy(queryParams, isNil) })

  // Update query params, picking only props that are able to be passed through query params.
  const updateQueryParams = useCallback(
    () => setQueryParams(pick(state, queryParamsKeys), 'replace'),
    [state, setQueryParams, queryParamsKeys]
  )

  // Restores search params to the default state given
  const clearSearchParams = useCallback(() => {
    clearSelection()
    dispatch({ type: 'replace', values: defaultState })
  }, [dispatch, defaultState, clearSelection])

  const updateSearchParams = useCallback(
    (values: Partial<ProductSearchParams>) => dispatch({ type: 'update', values }),
    [dispatch]
  )

  const setPage = useCallback(
    (page: number) => dispatch({ type: 'setPage', value: page }),
    [dispatch]
  )

  // Keep query params up to date with the internal state
  useEffect(() => {
    updateQueryParams()
  }, [updateQueryParams, state])

  return {
    setPage,
    clearSearchParams,
    updateSearchParams,
    searchParams: state,
  } as const
}
